/**
 * useTaskListSettings.ts
 * -----------------------------------------------------------------------------
 * Hook for managing TaskList user preferences with localStorage persistence.
 */

import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'center-task-list-settings';

/**
 * User-configurable settings for the task list
 */
export interface TaskListUserSettings {
  /** Days to look ahead for virtual tasks (default: 90) */
  lookaheadDays: number;
  /** Show completed tasks */
  showCompleted: boolean;
  /** Default sort field */
  sortBy: 'dueAt' | 'name' | 'urgency' | 'createdAt';
  /** Default sort order */
  sortOrder: 'asc' | 'desc';
  /** Group tasks by sphere in context mode */
  groupBySphere: boolean;
}

const DEFAULT_SETTINGS: TaskListUserSettings = {
  lookaheadDays: 90,
  showCompleted: false,
  sortBy: 'dueAt',
  sortOrder: 'asc',
  groupBySphere: false,
};

/**
 * Lookahead options for the settings panel
 */
export const LOOKAHEAD_OPTIONS = [
  { label: '1 week', value: 7 },
  { label: '2 weeks', value: 14 },
  { label: '1 month', value: 30 },
  { label: '3 months', value: 90 },
  { label: '6 months', value: 180 },
  { label: '1 year', value: 365 },
  { label: '2 years', value: 730 },
  { label: '5 years', value: 1825 },
];

/**
 * Load settings from localStorage
 */
function loadSettings(): TaskListUserSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle new fields
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch (err) {
    console.warn('[useTaskListSettings] Failed to load settings:', err);
  }
  return DEFAULT_SETTINGS;
}

/**
 * Save settings to localStorage
 */
function saveSettings(settings: TaskListUserSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (err) {
    console.warn('[useTaskListSettings] Failed to save settings:', err);
  }
}

/**
 * Hook for managing task list user settings with localStorage persistence
 */
export function useTaskListSettings() {
  const [userSettings, setUserSettings] = useState<TaskListUserSettings>(() => loadSettings());
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Save to localStorage whenever settings change
  useEffect(() => {
    saveSettings(userSettings);
  }, [userSettings]);

  const updateSetting = useCallback(<K extends keyof TaskListUserSettings>(
    key: K,
    value: TaskListUserSettings[K]
  ) => {
    setUserSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetSettings = useCallback(() => {
    setUserSettings(DEFAULT_SETTINGS);
  }, []);

  const openSettings = useCallback(() => {
    setIsSettingsOpen(true);
  }, []);

  const closeSettings = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  return {
    userSettings,
    setUserSettings,
    updateSetting,
    resetSettings,
    isSettingsOpen,
    openSettings,
    closeSettings,
  };
}

export default useTaskListSettings;
